import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import "./User.css";
import { useContext } from "react";
import CartContext from "./CartContext";
import OrdersContext from "./OrdersContext";
import p from './images/profile.jpg';

// ─── Performance helpers ───────────────────────────────────────────────────────
const SLOW_MS   = 2000;
// localStorage persists across page refreshes (sessionStorage clears on tab close)
const CACHE_KEY = "user_profile_cache";

function checkSlow(label, t0) {
  const ms = performance.now() - t0;
  if (ms > SLOW_MS)
    console.error(`[SLOW] ${label} took ${(ms / 1000).toFixed(2)}s — exceeds ${SLOW_MS / 1000}s threshold`);
  return ms;
}

function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || null; } catch { return null; }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
}
function clearCache() {
  try { localStorage.removeItem(CACHE_KEY); } catch {}
}


// ─── Inject skeleton shimmer CSS once on module load ─────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('user-skeleton-style')) {
  const s = document.createElement('style');
  s.id = 'user-skeleton-style';
  s.textContent = `
    @keyframes profile-shimmer {
      0%   { background-position: -400px 0; }
      100% { background-position:  400px 0; }
    }
    .skeleton-label, .skeleton-value {
      display: inline-block;
      border-radius: 4px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 800px 100%;
      animation: profile-shimmer 1.4s infinite linear;
    }
    .skeleton-label { width: 100px; height: 13px; }
    .skeleton-value { width: 200px; height: 13px; margin-left: 16px; }
  `;
  document.head.appendChild(s);
}

// ─── Static data ───────────────────────────────────────────────────────────────
const countryData = {
  US: {
    name: "United States",
    states: { CA: { name: "California", cities: ["San Jose"] } }
  },
  IN: {
    name: "India",
    states: {
      KA: {
        name: "Karnataka",
        cities: ["Udupi", "Kundapura", "Karkala", "Byndoor", "Brahmavar", "Hebri", "Kaup"]
      }
    }
  }
};

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "IN", name: "India" }
];

const EMPTY_PROFILE = {
  username: '', email: '', first_name: '', last_name: '',
  address: '', contact_number: '', profile_image: '',
  user_country: '', user_state: '', user_city: ''
};

// ─── Skeleton shimmer for a single field row ──────────────────────────────────
// Shown while fetching fresh data when no cache exists yet
function SkeletonRow() {
  return (
    <div className="profile-field-row">
      <span className="pf-label skeleton-label" />
      <span className="pf-value skeleton-value" />
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
function User() {
  const cached = readCache();

  // If cache exists → render immediately with real values (zero flash)
  // If no cache → render skeletons until fetch completes
  const [userProfile, setUserProfile] = useState(cached || EMPTY_PROFILE);
  const [isFetching,  setIsFetching]  = useState(!cached); // true only on first ever visit
  const [isEditing,   setIsEditing]   = useState(false);
  const [message,     setMessage]     = useState("");
  const [messageType, setMessageType] = useState("success");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword]   = useState("");
  const [deletePhrase,   setDeletePhrase]     = useState("");
  const [deleteLoading,  setDeleteLoading]    = useState(false);

  const [states, setStates] = useState(() => {
    if (!cached?.user_country || !countryData[cached.user_country]) return [];
    return Object.entries(countryData[cached.user_country].states)
      .map(([code, s]) => ({ code, name: s.name }));
  });
  const [cities, setCities] = useState(() => {
    if (!cached?.user_country || !cached?.user_state) return [];
    return countryData[cached.user_country]?.states[cached.user_state]?.cities || [];
  });

  const navigate = useNavigate();
  const tokenRef = useRef(localStorage.getItem("token"));
  const API_URL  = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';
  const { setCartCount }   = useContext(CartContext);
  const { setOrdersCount } = useContext(OrdersContext);

  // ── Fetch profile — runs in background if cache exists ─────────────────────
  const fetchProfile = useCallback(async () => {
    const token = tokenRef.current;
    if (!token) { setMessage("You must be logged in to view the profile."); return; }

    const t0 = performance.now();
    try {
      const res = await fetch(`${API_URL}/user/profile_detail_api`, {
        method: "GET",
        headers: { "Authorization": `Bearer ${token}` },
        credentials: "include",
      });

      checkSlow("GET /user/profile_detail_api", t0);

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      if (!data.username) {
        setMessage("User details could not be fetched.");
        setMessageType("error");
        return;
      }

      let avatarUrl = data.profile_image || "";
      if (avatarUrl && !avatarUrl.startsWith("http")) {
        avatarUrl = `${API_URL}/static/profile_images/${avatarUrl}`;
      }

      const profile = {
        username:       data.username,
        email:          data.email,
        first_name:     data.first_name     || "",
        last_name:      data.last_name      || "",
        address:        data.address        || "",
        contact_number: data.contact_number || "",
        profile_image:  avatarUrl,
        user_country:   data.user_country   || "",
        user_state:     data.user_state     || "",
        user_city:      data.user_city      || "",
      };

      writeCache(profile);
      setUserProfile(profile);

      if (data.user_country && countryData[data.user_country]) {
        setStates(Object.entries(countryData[data.user_country].states)
          .map(([code, s]) => ({ code, name: s.name })));
      }
      if (data.user_country && data.user_state &&
          countryData[data.user_country]?.states[data.user_state]) {
        setCities(countryData[data.user_country].states[data.user_state].cities);
      }
    } catch (err) {
      console.error("fetchProfile error:", err);
      if (!cached) {
        setMessage("Failed to fetch user details.");
        setMessageType("error");
      }
    } finally {
      setIsFetching(false);
    }
  }, [API_URL, cached]);

  useEffect(() => {
    if (!tokenRef.current) { window.location.href = "/login"; return; }
    fetchProfile();
    // eslint-disable-next-line
  }, []);

  // ── Cascade country → states ───────────────────────────────────────────────
  useEffect(() => {
    if (userProfile.user_country && countryData[userProfile.user_country]) {
      setStates(Object.entries(countryData[userProfile.user_country].states)
        .map(([code, s]) => ({ code, name: s.name })));
    } else {
      setStates([]);
      setUserProfile(prev => ({ ...prev, user_state: "", user_city: "" }));
    }
  }, [userProfile.user_country]);

  // ── Cascade state → cities ─────────────────────────────────────────────────
  useEffect(() => {
    if (userProfile.user_country && userProfile.user_state &&
        countryData[userProfile.user_country]?.states[userProfile.user_state]) {
      setCities(countryData[userProfile.user_country].states[userProfile.user_state].cities);
    } else {
      setCities([]);
      setUserProfile(prev => ({ ...prev, user_city: "" }));
    }
  }, [userProfile.user_state]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show local preview immediately — no wait for upload
    const localUrl = URL.createObjectURL(file);
    setUserProfile(prev => ({ ...prev, profile_image: localUrl }));

    const formData = new FormData();
    formData.append("image", file);

    const t0 = performance.now();
    try {
      const res = await fetch(`${API_URL}/user/upload_user_profile_image`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${tokenRef.current}` },
        body: formData,
      });

      checkSlow("POST /user/upload_user_profile_image", t0);

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Replace blob URL with real S3 URL, update cache
      const finalUrl = data.image_url || localUrl;
      setUserProfile(prev => {
        const updated = { ...prev, profile_image: finalUrl };
        writeCache(updated);
        return updated;
      });
    } catch (error) {
      console.error("Image upload failed:", error);
    } finally {
      e.target.value = '';
      URL.revokeObjectURL(localUrl);
    }
  };

  const handleSave = async () => {
    const token = tokenRef.current;
    if (!token) { setMessage("You must be logged in to save changes."); setMessageType("error"); return; }

    const t0 = performance.now();
    try {
      const res = await fetch(`${API_URL}/user/save_info`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          username:       userProfile.username,
          email:          userProfile.email,
          first_name:     userProfile.first_name,
          last_name:      userProfile.last_name,
          address:        userProfile.address,
          contact_number: userProfile.contact_number,
          user_country:   userProfile.user_country,
          user_state:     userProfile.user_state,
          user_city:      userProfile.user_city,
        })
      });
      checkSlow("POST /user/save_info", t0);
      const data = await res.json();
      if (data.message) {
        writeCache(userProfile); // persist updated values immediately
        setIsEditing(false);
        setMessage("Profile updated successfully!");
        setMessageType("success");
      } else {
        setMessage(data.error || "Could not update profile.");
        setMessageType("error");
      }
    } catch {
      setMessage("Could not update profile.");
      setMessageType("error");
    }
  };

  const logout = () => {
    clearCache();
    localStorage.removeItem("token");
    setCartCount(0);
    setOrdersCount(0);
    navigate("/");
  };

  const DELETE_CONFIRM_PHRASE = "DELETE MY ACCOUNT";

  const handleDeleteAccount = async () => {
    const token = tokenRef.current;
    if (!token) {
      setMessage("You must be logged in.");
      setMessageType("error");
      return;
    }
    if (deletePhrase.trim() !== DELETE_CONFIRM_PHRASE) {
      setMessage(`Type exactly: ${DELETE_CONFIRM_PHRASE}`);
      setMessageType("error");
      return;
    }

    setDeleteLoading(true);
    setMessage("");
    const t0 = performance.now();
    try {
      const res = await fetch(`${API_URL}/user/delete_account`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          password: deletePassword,
          confirm_phrase: deletePhrase.trim(),
        }),
      });
      checkSlow("POST /user/delete_account", t0);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(data.error || data.message || "Could not delete account.");
        setMessageType("error");
        return;
      }
      setShowDeleteModal(false);
      clearCache();
      localStorage.removeItem("token");
      setCartCount(0);
      setOrdersCount(0);
      navigate("/");
    } catch (e) {
      console.error("delete account:", e);
      setMessage("Could not delete account. Try again later.");
      setMessageType("error");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const SKELETON_ROWS = 9; // matches number of profile fields

  return (
    <>
      <Header />
      <div className="profile-page-bg">
        <div className="user-profile-card">

          {/* ── Avatar ── */}
          <div className="profile-image-section">
            <div className="profile-image-wrapper">
              <img
                src={userProfile.profile_image || p}
                alt={userProfile.username || "Profile"}
                className="profile-image"
                loading="eager"
              />
              <div className="profile-image-overlay">
                <i className="fas fa-camera" />
              </div>
              <label htmlFor="profileImageUpload" className="camera-icon">
                <i className="fas fa-camera" />
              </label>
              <input
                id="profileImageUpload"
                type="file"
                accept="image/*"
                className="profile-image-input"
                onChange={handleImageChange}
              />
            </div>
          </div>

          <h2 className="user-profile-title">User Profile</h2>

          {userProfile.username && (
            <p className="profile-username-chip">@{userProfile.username}</p>
          )}

          {message && (
            <div className={`form-message ${messageType}`}>{message}</div>
          )}

          {isEditing ? (
            <form className="profile-edit-form" onSubmit={e => { e.preventDefault(); handleSave(); }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Username</label>
                  <input type="text" name="username" value={userProfile.username} onChange={handleChange} required placeholder="Enter username" />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={userProfile.email} onChange={handleChange} required placeholder="Enter email" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input type="text" name="first_name" value={userProfile.first_name} onChange={handleChange} placeholder="First name" />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input type="text" name="last_name" value={userProfile.last_name} onChange={handleChange} placeholder="Last name" />
                </div>
              </div>
              <div className="form-group full-width">
                <label>Address</label>
                <input type="text" name="address" value={userProfile.address} onChange={handleChange} placeholder="Your address" />
              </div>
              <div className="form-group full-width">
                <label>Contact Number</label>
                <input type="text" name="contact_number" value={userProfile.contact_number} onChange={handleChange} placeholder="+91 00000 00000" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Country</label>
                  <select name="user_country" value={userProfile.user_country} onChange={handleChange}>
                    <option value="">Select Country</option>
                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>State</label>
                  <select name="user_state" value={userProfile.user_state} onChange={handleChange} disabled={!userProfile.user_country}>
                    <option value="">Select State</option>
                    {states.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group full-width">
                <label>City</label>
                <select name="user_city" value={userProfile.user_city} onChange={handleChange} disabled={!userProfile.user_state}>
                  <option value="">Select City</option>
                  {cities.map((city, i) => <option key={i} value={city}>{city}</option>)}
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-save">Save Changes</button>
                <button type="button" className="btn-cancel" onClick={() => setIsEditing(false)}>Cancel</button>
              </div>
            </form>

          ) : (
            <div className="profile-info-section">
              <div className="profile-fields">
                {isFetching
                  ? Array.from({ length: SKELETON_ROWS }).map((_, i) => <SkeletonRow key={i} />)
                  : [
                      { label: "Username",       value: userProfile.username },
                      { label: "Email",          value: userProfile.email },
                      { label: "First Name",     value: userProfile.first_name  || "—" },
                      { label: "Last Name",      value: userProfile.last_name   || "—" },
                      { label: "Address",        value: userProfile.address     || "—" },
                      { label: "Contact Number", value: userProfile.contact_number || "—" },
                      { label: "Country",        value: COUNTRIES.find(c => c.code === userProfile.user_country)?.name || userProfile.user_country || "—" },
                      { label: "State",          value: states.find(s => s.code === userProfile.user_state)?.name || userProfile.user_state || "—" },
                      { label: "City",           value: userProfile.user_city   || "—" },
                    ].map(({ label, value }) => (
                      <div className="profile-field-row" key={label}>
                        <span className="pf-label">{label}</span>
                        <span className="pf-value">{value}</span>
                      </div>
                    ))
                }
              </div>

              <div className="profile-button-row">
                <button onClick={() => setIsEditing(true)} className="btn-edit">
                  <i className="fas fa-pen" /> Edit Profile
                </button>
                <button onClick={logout} className="btn-logout">
                  <i className="fas fa-sign-out-alt" /> Log Out
                </button>
              </div>

              <div className="profile-danger-zone">
                <h3 className="danger-zone-title">Danger zone</h3>
                <p className="danger-zone-text">
                  Permanently delete your account and your data from this app. A backup snapshot is kept in our database for recovery and compliance. This cannot be undone from the app.
                </p>
                <button
                  type="button"
                  className="btn-delete-account"
                  onClick={() => {
                    setDeletePassword("");
                    setDeletePhrase("");
                    setShowDeleteModal(true);
                  }}
                >
                  <i className="fas fa-trash-alt" aria-hidden /> Delete account
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {showDeleteModal && (
        <div
          className="delete-account-modal-overlay"
          role="presentation"
          onClick={() => !deleteLoading && setShowDeleteModal(false)}
        >
          <div
            className="delete-account-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="delete-account-title" className="delete-account-modal-title">
              Delete your account?
            </h3>
            <p className="delete-account-modal-hint">
              Enter your password (if you sign in with email and password). Google-only accounts can leave the password blank.
            </p>
            <label className="delete-account-label" htmlFor="delete-account-password">
              Password
            </label>
            <input
              id="delete-account-password"
              type="password"
              className="delete-account-input"
              autoComplete="current-password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              disabled={deleteLoading}
              placeholder="Your password"
            />
            <label className="delete-account-label" htmlFor="delete-account-phrase">
              Type <strong>{DELETE_CONFIRM_PHRASE}</strong> to confirm
            </label>
            <input
              id="delete-account-phrase"
              type="text"
              className="delete-account-input"
              value={deletePhrase}
              onChange={(e) => setDeletePhrase(e.target.value)}
              disabled={deleteLoading}
              placeholder={DELETE_CONFIRM_PHRASE}
              autoComplete="off"
            />
            <div className="delete-account-actions">
              <button
                type="button"
                className="btn-delete-cancel"
                disabled={deleteLoading}
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-delete-confirm"
                disabled={deleteLoading}
                onClick={handleDeleteAccount}
              >
                {deleteLoading ? "Deleting…" : "Delete forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default User;

